// URL parameters for a dashboard page
export interface DashboardPageParams {
  dashboardId?: string;
  queryIds?: string;
  templateQueryIndices?: Array<number | string>;
  pageId?: string;
}

// Build the path for navigating to a dashboard page.
export function getDashboardNavigatePath(pathInfo: DashboardPageParams): string {
  if (!pathInfo.dashboardId) {
    throw new Error("Dashboard ID is required to generate the path.");
  }
  let path = `/dashboard/${pathInfo.dashboardId || ''}`;
  if (pathInfo.queryIds) {
    path += `/${pathInfo.queryIds}`;
  }
  if (pathInfo.templateQueryIndices !== undefined &&
      pathInfo.templateQueryIndices !== null &&
      pathInfo.templateQueryIndices.length > 0) {
    path += `/template/${pathInfo.templateQueryIndices.join(",")}`;
  }
  if (pathInfo.pageId) {
    path += `/page/${pathInfo.pageId}`;
  }
  return path;
}

// Parse the URL parameters from the current page to get dashboard information.
export function parsePageParams(url?:string): DashboardPageParams {
  url = url || window.location.href

  // Split the URL into parsable tokens
  const urlTokens = url.split("/");

  // The last token type, used to determine the next token's meaning
  // For example, if the last token was "dashboard", the next token is expected to be the dashboard ID
  let lastTokenType = "";

  // Sequential token parser to extract dashboard parameters from the URL
  const params = {} as DashboardPageParams;
  urlTokens.forEach((section) => {
    if (section.startsWith("dashboard")) {
      lastTokenType = "dashboard";
    } else if (section.startsWith("template")) {
      lastTokenType = "template";
    } else if (section.startsWith("page")) {
      lastTokenType = "page";
    } else if (lastTokenType === "dashboard") {
      params.dashboardId = section;
      lastTokenType = "dashboardId";
    } else if (lastTokenType === "dashboardId") {
      params.queryIds = section;
      lastTokenType = "queryIds";
    } else if (lastTokenType === "template") {
      params.templateQueryIndices = section.split(",").map((idx) => {
        // If the value is "all", we keep it as a string to use all template queries in the navigation.
        if (idx === "all") return "all";
        // Otherwise, we parse it as a number to use a specific template query index in the navigation.
        return Number(idx);
      });
      lastTokenType = "templateQueryIndices";
    } else if (lastTokenType === "page") {
      params.pageId = section;
      lastTokenType = "pageId";
    }
  });

  return params;
}

export interface DashboardPageQueries {
  queryIds: string[];
  globalQueryId?: string;
}

export function parsePageQueries(page_query_string: string | undefined): DashboardPageQueries {
  const pageQueries: DashboardPageQueries = {
    queryIds: []
  };
  if (page_query_string) {
    const queryParts = page_query_string.split("-");
    queryParts.forEach((part) => {
      if (part.startsWith("g_")) {
        pageQueries.globalQueryId = part.slice(2); // remove the "g_" prefix
      } else {
        pageQueries.queryIds.push(part);
      }
    });
  }
  return pageQueries;
}

export function assemblePageQueryString(queryIds?: Array<string>, globalQueryId?: string): string {
  const queryString = queryIds ? queryIds.join("-") : "";
  if (globalQueryId) {
    return queryString ? `${queryString}-g_${globalQueryId}` : `g_${globalQueryId}`;
  }
  return queryString;
}